import type {
  BrandProfileResponse,
  ChannelAssetsResponse,
  CoverFinalizeResponse,
  CoverFormatsResponse,
  CoverRefineResponse,
  CoverSource,
  CoverVariantsRequest,
  CoverVariantsResponse,
  EventDraftResponse,
  EventFlowState,
  ExtraEventChannel,
  LogoPlacement,
  PhotoComposeRequest,
  PhotoCoverFields,
} from '@pulsify/shared/dto'
import { eventDraftResponseSchema } from '@pulsify/shared/dto'
import { adminBypassHeaders } from '~/app/admin-bypass'
import { activeProfileHeaders } from '~/shared/lib/active-profile-store'
import { api, getAuthToken, unwrap } from '~/shared/lib/rpc'

const flowRoutes = () => api.api.businesses[':businessId']['ai-studio']['event-flow']

async function postForm<T>(url: string | URL, form: FormData): Promise<T> {
  const token = await getAuthToken()
  const headers: Record<string, string> = { ...adminBypassHeaders(), ...activeProfileHeaders() }
  if (token) headers.Authorization = `Bearer ${token}`
  const res = await fetch(url, { method: 'POST', body: form, headers })
  return unwrap<T>(res)
}

interface LogoChoice {
  placement: LogoPlacement
  large: boolean
}

function appendLogo(form: FormData, logo: LogoChoice) {
  form.append('logo', logo.placement)
  form.append('logoLarge', logo.large ? 'true' : 'false')
}

export const eventFlowApi = {
  async draft(
    businessId: string,
    input: { prompt: string; pictures: File[]; locale: 'de' | 'en'; package: boolean },
  ): Promise<EventDraftResponse> {
    const url = api.api.businesses[':businessId']['ai-studio']['event-draft'].$url({
      param: { businessId },
    })
    const form = new FormData()
    form.append('prompt', input.prompt)
    form.append('locale', input.locale)
    if (input.package) form.append('package', 'true')
    for (const picture of input.pictures) form.append('files', picture)
    const raw = await postForm<EventDraftResponse>(url, form)
    const parsed = eventDraftResponseSchema.safeParse(raw)
    return parsed.success ? parsed.data : raw
  },

  async start(businessId: string): Promise<EventFlowState> {
    const res = await flowRoutes().start.$post({ param: { businessId } })
    return unwrap<EventFlowState>(res)
  },

  async variants(
    businessId: string,
    request: CoverVariantsRequest,
  ): Promise<CoverVariantsResponse> {
    const res = await flowRoutes()['cover-variants'].$post({
      param: { businessId },
      json: request,
    })
    return unwrap<CoverVariantsResponse>(res)
  },

  async photoVariants(
    businessId: string,
    input: { photo: File; fields: PhotoCoverFields },
  ): Promise<CoverVariantsResponse> {
    const form = new FormData()
    form.append('file', input.photo)
    for (const [key, value] of Object.entries(input.fields)) form.append(key, value)
    return postForm(flowRoutes()['photo-variants'].$url({ param: { businessId } }), form)
  },

  async refine(
    businessId: string,
    image: Blob,
    instruction: string,
    source: CoverSource = 'graphic',
  ): Promise<CoverRefineResponse> {
    const form = new FormData()
    form.append('file', image, 'variant.jpg')
    form.append('instruction', instruction)
    form.append('source', source)
    return postForm(flowRoutes()['cover-refine'].$url({ param: { businessId } }), form)
  },

  async finalize(
    businessId: string,
    input: {
      image: Blob
      flowId: string
      title: string
      logo: LogoChoice
      source?: CoverSource
    },
  ): Promise<CoverFinalizeResponse> {
    const form = new FormData()
    form.append('file', input.image, 'variant.jpg')
    form.append('flowId', input.flowId)
    form.append('title', input.title)
    form.append('source', input.source ?? 'graphic')
    appendLogo(form, input.logo)
    return postForm(flowRoutes()['cover-finalize'].$url({ param: { businessId } }), form)
  },

  async formats(
    businessId: string,
    input: {
      master: Blob
      channels: ExtraEventChannel[]
      flowId: string | null
      title: string
      logo: LogoChoice
      source?: CoverSource
    },
  ): Promise<CoverFormatsResponse> {
    const form = new FormData()
    form.append('file', input.master, 'cover.jpg')
    form.append('channels', JSON.stringify(input.channels))
    if (input.flowId) form.append('flowId', input.flowId)
    form.append('title', input.title)
    form.append('source', input.source ?? 'graphic')
    appendLogo(form, input.logo)
    return postForm(flowRoutes()['cover-formats'].$url({ param: { businessId } }), form)
  },

  async photo(
    businessId: string,
    input: { photo: File; request: PhotoComposeRequest; title: string },
  ): Promise<ChannelAssetsResponse> {
    const form = new FormData()
    form.append('file', input.photo)
    form.append('request', JSON.stringify(input.request))
    form.append('title', input.title)
    return postForm(flowRoutes().photo.$url({ param: { businessId } }), form)
  },

  async brand(businessId: string): Promise<BrandProfileResponse> {
    const res = await api.api.businesses[':businessId']['ai-studio'].brand.$get({
      param: { businessId },
    })
    return unwrap<BrandProfileResponse>(res)
  },

  async saveBrandColors(businessId: string, colors: string[]): Promise<BrandProfileResponse> {
    const res = await api.api.businesses[':businessId']['ai-studio'].brand.$put({
      param: { businessId },
      json: { colors },
    })
    return unwrap<BrandProfileResponse>(res)
  },
}

/** The image bytes of a cover the server sent inline. */
export function coverBlob(image: { imageBase64: string; mimeType: string }): Blob {
  const binary = atob(image.imageBase64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type: image.mimeType })
}
